import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import portalApi from './portalApi';
import { T, sans } from './portalTheme';

/*
 * The vendor's own FluxWatch security rating, shared by every place the portal shows it: the header chip, the sidebar
 * card, the big dashboard banner and the Security rating page. Kept free of PortalWidgets imports so the portal shell
 * can use it without an import cycle.
 */

export type Severity = 'critical' | 'high' | 'medium' | 'low';
export interface PortalRatingData {
  status: 'unavailable' | 'scanning' | 'ready' | 'failed';
  reason?: 'free_mail' | 'disabled_by_customers' | 'no_domain' | 'not_started' | null;
  domain: string | null;
  email_domain: string | null;
  options: { domain: string; label: string }[];
  requested_at?: string;
  rating?: {
    score: number | null; grade: string | null; risk_level: string | null; scanned_at: string | null; checks: number;
    counts: Record<Severity, number>;
    weakest_categories: { name: string; score: number }[];
    categories?: { name: string; score: number }[];
    findings_total?: number;
    top_findings: { title: string; severity: Severity; category: string | null; summary: string }[];
  };
}

export const RATING_URL = '/questionnaire/portal/rating';
export const RATING_API = '/v1/questionnaire-responses/portal/rating';
export const portalRatingQuery = {
  queryKey: ['portal', 'rating'],
  queryFn: async () => (await portalApi.get(RATING_API)).data as PortalRatingData,
  // While the scan runs, look again every 10 seconds so the result appears by itself.
  refetchInterval: (q: { state: { data?: PortalRatingData } }) => (q.state.data?.status === 'scanning' ? 10_000 : false as const),
  staleTime: 60_000,
};
export const usePortalRating = () => useQuery(portalRatingQuery);

export const GRADE_COLOR: Record<string, string> = { A: '#0F8A5F', B: '#4D7C0F', C: '#B45309', D: '#C2410C', F: '#B91C1C' };
// Brighter versions for the dark banner, where the darker greens/ambers would disappear.
export const GRADE_COLOR_ON_DARK: Record<string, string> = { A: '#34D399', B: '#A3E635', C: '#FBBF24', D: '#FB923C', F: '#F87171' };
export const SEV: Record<Severity, { label: string; color: string; bg: string; onDark: string }> = {
  critical: { label: 'Critical', color: '#B91C1C', bg: '#FEF2F2', onDark: '#F87171' },
  high: { label: 'High', color: '#C2410C', bg: '#FFF7ED', onDark: '#FB923C' },
  medium: { label: 'Medium', color: '#B45309', bg: '#FFFBEB', onDark: '#FBBF24' },
  low: { label: 'Low', color: '#1D4ED8', bg: '#EFF6FF', onDark: '#93C5FD' },
};
export const gradeLine = (g: string | null | undefined) =>
  g === 'A' ? 'Strong security posture' : g === 'B' ? 'Good, with a few gaps' : g === 'C' ? 'Several gaps to close'
    : g === 'D' ? 'Weak: customers will notice' : g === 'F' ? 'High risk to customers' : 'Not rated yet';

export const BANNER = '#0B1B33';

export function MiniSpinner({ size = 16, color = T.primary }: { size?: number; color?: string }) {
  return (
    <span style={{ width: size, height: size, borderRadius: 99, border: `2px solid ${color}33`, borderTopColor: color, display: 'inline-block',
      animation: 'prSpin .8s linear infinite', flexShrink: 0 }} />
  );
}

export function ScoreRing({ score, grade, size = 132, dark = false, label = 'out of 100' }: {
  score: number | null; grade: string | null; size?: number; dark?: boolean; label?: string;
}) {
  const stroke = Math.max(6, Math.round(size * 0.075));
  const r = (size - stroke - 4) / 2, c = 2 * Math.PI * r, pct = score == null ? 0 : Math.max(0, Math.min(100, score)) / 100;
  const color = grade ? (dark ? GRADE_COLOR_ON_DARK : GRADE_COLOR)[grade] || T.primary : dark ? '#64748B' : T.faint;
  const [shown, setShown] = useState(0);
  useEffect(() => { const t = setTimeout(() => setShown(pct), 60); return () => clearTimeout(t); }, [pct]);
  return (
    <svg width={size} height={size} role="img" aria-label={score == null ? 'Not rated' : `Score ${score} out of 100`} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={dark ? 'rgba(255,255,255,.12)' : T.hover} strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={`${c * shown} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dasharray 1.1s cubic-bezier(.2,.8,.2,1)' }} />
      <text x="50%" y={label ? '46%' : '50%'} dominantBaseline="central" textAnchor="middle"
        style={{ fontFamily: sans, fontSize: size * 0.31, fontWeight: 800, fill: dark ? '#FFFFFF' : T.ink, letterSpacing: '-.02em' }}>
        {score == null ? '–' : score}
      </text>
      {label && size >= 90 && (
        <text x="50%" y="69%" dominantBaseline="central" textAnchor="middle" style={{ fontFamily: sans, fontSize: size * 0.095, fontWeight: 600, fill: dark ? '#94A3B8' : T.muted }}>
          {label}
        </text>
      )}
    </svg>
  );
}

/** Header chip, on every portal page: the company domain, score and grade. Opens the rating page. */
export function RatingHeaderChip() {
  const { data: d } = usePortalRating();
  if (!d || (d.status !== 'ready' && d.status !== 'scanning')) return null;
  const r = d.rating;
  const color = r?.grade ? GRADE_COLOR[r.grade] : T.primary;
  return (
    <Link to={RATING_URL} className="pr-chip pw-hide-sm" title="Your company's FluxWatch security rating" style={{
      display: 'inline-flex', alignItems: 'center', gap: 10, height: 40, padding: '0 6px 0 12px', borderRadius: 99, textDecoration: 'none',
      border: `1px solid ${T.bdr}`, background: T.surface, fontFamily: sans,
    }}>
      {d.status === 'ready' && r ? (<>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: T.faint }}>Your rating</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: T.ink2, maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.domain}</span>
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, background: `${color}14`, border: `1px solid ${color}40`, borderRadius: 99, padding: '4px 11px' }}>
          <span style={{ fontSize: 15, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums' }}>{r.score}</span>
          <span style={{ fontSize: 12, fontWeight: 800, color }}>{r.grade}</span>
        </span>
      </>) : (<>
        <MiniSpinner size={14} />
        <span style={{ fontSize: 13, fontWeight: 600, color: T.slate, paddingRight: 8 }}>Rating {d.domain}…</span>
      </>)}
    </Link>
  );
}

/** Sidebar card, under the menu: the score at a glance. */
export function RatingSidebarCard() {
  const { data: d } = usePortalRating();
  if (!d || d.status !== 'ready' || !d.rating) return null;
  const r = d.rating;
  return (
    <Link to={RATING_URL} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, borderRadius: 12, background: BANNER, textDecoration: 'none', marginBottom: 12 }}>
      <ScoreRing score={r.score} grade={r.grade} size={52} dark label="" />
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: '#94A3B8' }}>Your rating</span>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: '#FFFFFF', marginTop: 2 }}>Grade {r.grade}</span>
        <span style={{ display: 'block', fontSize: 12, color: '#93C5FD', marginTop: 1 }}>See what customers see →</span>
      </span>
    </Link>
  );
}

const SCAN_STEPS = ['Reading DNS and email security', 'Checking certificates and encryption', 'Testing website security headers',
  'Looking up breach and reputation data', 'Scoring 14 risk categories'];

/**
 * The big banner on the portal dashboard: the company's rating, front and centre, with the free full-report offer.
 * Collapsible; once collapsed it stays collapsed for this person on this browser.
 */
export function RatingHero({ accountId, firstName, onClaim, claiming, collapsible = true }: {
  accountId?: string; firstName?: string; onClaim: () => void; claiming: boolean; collapsible?: boolean;
}) {
  const { data: d } = usePortalRating();
  const key = `pw_rating_hero_hidden_${accountId || 'me'}`;
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem(key) === '1'; } catch { return false; } });
  const hide = (v: boolean) => { setHidden(v); try { v ? localStorage.setItem(key, '1') : localStorage.removeItem(key); } catch { /* ignore */ } };
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (d?.status !== 'scanning') return;
    const t = setInterval(() => setTick(x => x + 1), 1800);
    return () => clearInterval(t);
  }, [d?.status]);

  if (!d || (d.status !== 'ready' && d.status !== 'scanning')) return null;
  const r = d.rating;

  if (collapsible && hidden && d.status === 'ready' && r) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, background: T.surface, border: `1px solid ${T.bdr}`, borderRadius: 14, padding: '12px 18px', marginBottom: 24, fontFamily: sans }}>
        <ScoreRing score={r.score} grade={r.grade} size={50} label="" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: T.ink }}>
            Your security rating: <span style={{ color: r.grade ? GRADE_COLOR[r.grade] : T.ink }}>{r.score} · Grade {r.grade}</span>
          </div>
          <div style={{ fontSize: 12.5, color: T.muted, marginTop: 2 }}>{d.domain} · {r.counts.critical + r.counts.high} critical or high findings</div>
        </div>
        <Link to={RATING_URL} style={{ fontSize: 13, fontWeight: 700, color: T.primary, textDecoration: 'none', whiteSpace: 'nowrap' }}>View rating →</Link>
        <button onClick={() => hide(false)} style={{ fontSize: 12.5, fontWeight: 600, color: T.muted, background: 'none', border: 'none', cursor: 'pointer', fontFamily: sans }}>Expand</button>
      </div>
    );
  }

  const light = '#94A3B8';
  return (
    <section className="pr-hero-banner" style={{ position: 'relative', background: BANNER, borderRadius: 18, padding: '30px 34px', marginBottom: 24, color: '#fff', fontFamily: sans, overflow: 'hidden' }}>
      {/* decorative rings, top right */}
      <svg aria-hidden="true" width="420" height="420" style={{ position: 'absolute', right: -120, top: -150, opacity: 0.09, pointerEvents: 'none' }}>
        {[200, 160, 120, 80].map(rr => <circle key={rr} cx="210" cy="210" r={rr} fill="none" stroke="#93C5FD" strokeWidth="1.5" />)}
      </svg>
      {collapsible && d.status === 'ready' && (
        <button onClick={() => hide(true)} aria-label="Collapse" style={{ position: 'absolute', top: 14, right: 16, background: 'rgba(255,255,255,.08)', border: 'none', color: light,
          borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: sans }}>Collapse</button>
      )}

      <div className="pr-hero-grid" style={{ position: 'relative', display: 'grid', gridTemplateColumns: 'minmax(0, 1.25fr) auto', gap: 36, alignItems: 'center' }}>
        <div style={{ minWidth: 0 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11.5, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase',
            color: '#93C5FD', background: 'rgba(147,197,253,.12)', border: '1px solid rgba(147,197,253,.25)', borderRadius: 99, padding: '5px 12px' }}>
            <span style={{ width: 7, height: 7, borderRadius: 99, background: d.status === 'ready' ? '#34D399' : '#FBBF24', boxShadow: `0 0 0 4px ${d.status === 'ready' ? 'rgba(52,211,153,.18)' : 'rgba(251,191,36,.18)'}` }} />
            {d.status === 'ready' ? 'Your company security rating' : 'Assessing your company'}
          </span>
          <h2 style={{ fontSize: 30, lineHeight: 1.2, fontWeight: 800, letterSpacing: '-.02em', margin: '16px 0 0', maxWidth: 620 }}>
            {d.status === 'ready'
              ? <>{firstName ? `${firstName}, this` : 'This'} is how <span style={{ color: '#93C5FD' }}>{d.domain}</span> looks to your customers</>
              : <>We're rating <span style={{ color: '#93C5FD' }}>{d.domain}</span> right now</>}
          </h2>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#CBD5E1', margin: '12px 0 0', maxWidth: 600 }}>
            {d.status === 'ready' && r
              ? <>FluxWatch checked <b style={{ color: '#fff' }}>{r.checks} public security signals</b> about your company: the same kind of outside-in rating the companies you supply use to assess you.</>
              : <>FluxWatch is running its public, passive checks. It usually takes 2 to 3 minutes, and this page updates by itself.</>}
          </p>

          {d.status === 'ready' && r ? (<>
            <div className="pr-hero-sev" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10, marginTop: 22, maxWidth: 560 }}>
              {(['critical', 'high', 'medium', 'low'] as Severity[]).map(s => (
                <div key={s} style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 12, padding: '10px 12px' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: r.counts[s] ? SEV[s].onDark : '#475569', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{r.counts[s]}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: light, marginTop: 3 }}>{SEV[s].label}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 24 }}>
              <button onClick={onClaim} disabled={claiming} className="pr-cta-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#FFFFFF', color: BANNER,
                border: 'none', borderRadius: 10, padding: '12px 20px', fontSize: 14.5, fontWeight: 800, cursor: claiming ? 'wait' : 'pointer', fontFamily: sans }}>
                {claiming ? <MiniSpinner size={14} color={BANNER} /> : null} Get your full report, free
              </button>
              <Link to={RATING_URL} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#E2E8F0', border: '1px solid rgba(255,255,255,.22)',
                borderRadius: 10, padding: '11px 18px', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>See rating details →</Link>
            </div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 10 }}>Free FluxWatch workspace · no card needed · takes a minute</div>
          </>) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: '20px 0 0', display: 'grid', gap: 9 }}>
              {SCAN_STEPS.map((step, i) => {
                const done = i < tick % (SCAN_STEPS.length + 1), current = i === tick % (SCAN_STEPS.length + 1);
                return (
                  <li key={step} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: done ? '#E2E8F0' : current ? '#FFFFFF' : '#64748B' }}>
                    {done ? <span style={{ color: '#34D399', fontWeight: 800 }}>✓</span> : current ? <MiniSpinner size={13} color="#93C5FD" /> : <span style={{ width: 13, height: 13, borderRadius: 99, border: '2px solid #334155', display: 'inline-block' }} />}
                    {step}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="pr-hero-score" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, minWidth: 230 }}>
          {d.status === 'ready' && r ? (<>
            <ScoreRing score={r.score} grade={r.grade} size={190} dark />
            <span style={{ fontSize: 15, fontWeight: 800, color: r.grade ? GRADE_COLOR_ON_DARK[r.grade] : '#fff', background: 'rgba(255,255,255,.07)',
              border: `1px solid ${r.grade ? GRADE_COLOR_ON_DARK[r.grade] : '#fff'}55`, borderRadius: 99, padding: '6px 16px' }}>
              Grade {r.grade} · {gradeLine(r.grade)}
            </span>
            {r.weakest_categories[0] && (
              <span style={{ fontSize: 12.5, color: light }}>Weakest area: <b style={{ color: '#E2E8F0' }}>{r.weakest_categories[0].name}</b> ({r.weakest_categories[0].score})</span>
            )}
          </>) : (
            <div style={{ width: 190, height: 190, borderRadius: 999, border: '10px solid rgba(255,255,255,.08)', borderTopColor: '#93C5FD',
              animation: 'prSpin 1.4s linear infinite' }} />
          )}
        </div>
      </div>
    </section>
  );
}

/** Keyframes and responsive rules for the rating pieces (rendered once by the portal shell). */
export function RatingStyles() {
  return (
    <style>{`
      @keyframes prSpin { to { transform: rotate(360deg); } }
      .pr-chip:hover { border-color: ${T.bdrStrong} !important; background: ${T.surface2} !important; }
      .pr-cta-btn:hover { transform: translateY(-1px); }
      .pr-cta-btn { transition: transform .15s; }
      @media (max-width: 900px) {
        .pr-hero-grid { grid-template-columns: minmax(0, 1fr) !important; }
        .pr-hero-score { order: -1; }
        .pr-hero-banner { padding: 24px 20px !important; }
      }
      @media (max-width: 560px) { .pr-hero-sev { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; } }
    `}</style>
  );
}

/** "Get your full report": a signed hand-off to FluxWatch sign-up (same tab: a new tab after an await is a blocked pop-up). */
export async function startClaim(): Promise<void> {
  const c = (await portalApi.post(`${RATING_API}/claim`)).data as { claim: string; name: string; email: string; company: string; domain: string | null };
  const q = new URLSearchParams({ portal_claim: c.claim, name: c.name, email: c.email, company: c.company, domain: c.domain || '' });
  window.location.assign(`/signup?${q.toString()}`);
}
